import { useBiApiClientProvider } from "../../../../contexts/BiApiClientProviderContext.ts";
import type { BiApiClient } from "../../../../contexts/ApiClientProviderContext.ts";
import { getTabularExportWarning } from "../../../common/export/exportWarnings.ts";
import ExcelReportExport from "../../../common/export/ExcelReportExport.tsx";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import type { ConditionField, PublicTabularResponse } from "../../../../../../../../api/types/svTypes.ts";
import { useEnhancedBiApiClientProvider } from "../../../../contexts/ApiClientProviderContext.ts";
import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import { selectPreviewMode, selectSessionId } from "../../../../store/selectors/currentReportSelectors.ts";
import { selectLogQueries } from "../../../../store/selectors/devToolsSelectors.ts";
import { createApiReportAction } from "../../../../store/utils/storeUtils.ts";
import PreviewActions from "../../../viewer/common/PreviewActions.tsx";
import ViewerParameters from "../../../viewer/common/ViewerParameters.tsx";
import type { TabularRunState } from "../../../viewer/storage/runState.ts";
import useGridStateViewer from "../../../viewer/tabular/hooks/useGridStateViewer.ts";
import { useRequestConfigViewer } from "../../../viewer/tabular/hooks/useRequestConfigViewer.ts";
import PreviewComponent from "../../../viewer/tabular/PreviewComponent.tsx";
import ChangePreviewModeButton from "../../common/ChangePreviewModeButton.tsx";
import useCommonReportHandlers from "../../common/hooks/useCommonReportHandlers.ts";
import CustomId from "../editor/fields/CustomId.tsx";
import ViewContainer from "./ViewContainer.tsx";

interface Props {
  runState: TabularRunState;
}

const ReportViewer = ({ runState }: Props) => {
  const { requestRunTabularExport } = useBiApiClientProvider<BiApiClient>();
  const { runTabularReport: runReport } = useEnhancedBiApiClientProvider();
  const { dimensions, reportId, onRefreshGrid } = useBiCommonDataContext();
  const isQueryLoggingActive = useSelector(selectLogQueries);
  const sessionId = useSelector(selectSessionId);
  const previewMode = useSelector(selectPreviewMode);
  const dispatch = useAppDispatch();
  const handlers = useCommonReportHandlers(runState.fields);

  const { requestConfig: viewConfig } = useRequestConfigViewer({
    fields: runState.fields,
    sorting: runState.sort,
    conditions: runState.conditions,
    reportId: reportId,
    sessionId: sessionId,
    isQueryLoggingActive: isQueryLoggingActive,
  });

  const onSetResponse = useCallback(
    (response: PublicTabularResponse | undefined) => {
      dispatch(createApiReportAction("setResponse", response));
    },
    [dispatch]
  );

  const gridState = useGridStateViewer({
    fields: runState.fields,
    sorting: runState.sort,
    requestConfig: viewConfig,
    setEtag: handlers.setEtag,
    setResponse: onSetResponse,
    updateDevTools: handlers.updateDevTools,
    runReport,
  });

  const renderCustomId = useCallback((field: ConditionField) => {
    return (
      <CustomId
        customId={field.config.customId}
        defaultId={field.config.guid}
        readonly={true}
        updateCustomId={undefined}
      />
    );
  }, []);

  return (
    <>
      <ExcelReportExport
        mode="run"
        requestConfig={viewConfig}
        dimensions={dimensions}
        requestExport={requestRunTabularExport}
        warning={getTabularExportWarning(gridState)}
      />
      <ViewContainer
        Parameters={
          runState.showParameters ? (
            <ViewerParameters
              fieldsWithConditions={runState.fieldsWithCustomConditionParameters}
              initialDetailedView={runState.settings.detailedView}
              renderCustomId={renderCustomId}
            />
          ) : (
            <></>
          )
        }
        Preview={
          <PreviewComponent
            gridState={gridState}
            //Disabled due to view mode
            disableColumnManagement={true}
            fields={runState.fields}
            groups={runState.grouping}
            previewMode={previewMode}
            settings={runState.settings}
            sorts={runState.sort}
            onDrillDown={handlers.onDrillDown}
            onPredefinedCustomFieldType={undefined}
          />
        }
        ActionPanel={<PreviewActions onRefresh={onRefreshGrid} ChangePreviewModeButton={<ChangePreviewModeButton />} />}
        isLoading={gridState.loading}
        runState={runState}
        isQueryLoggingActive={!!isQueryLoggingActive}
        sessionId={sessionId}
      />
    </>
  );
};

export default ReportViewer;
